import { useState, useEffect } from "react";
import { Heart, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { THANK_YOU_PRESETS } from "../lib/constants";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

export default function ThankYouModal({ recipient, onClose }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { setText(""); }, [recipient]);

  const send = async () => {
    if (!text.trim()) { toast.error("Pick a note or write your own 💛"); return; }
    setSending(true);
    try {
      await api.post("/thankyou", { to_user_id: recipient.user_id, text });
      toast.success(`Your thank-you is on its way to ${recipient.name?.split(" ")[0] || "them"} 💛`);
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    setSending(false);
  };

  return (
    <Dialog open={!!recipient} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="thankyou-modal" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-bronze flex items-center gap-2">
            <Heart className="w-6 h-6 text-copper fill-copper" /> Thank {recipient?.name?.split(" ")[0]}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          A few kind words go a long way. Pick a note or write your own — they'll feel every bit of it.
        </p>

        <div className="flex flex-wrap gap-2">
          {THANK_YOU_PRESETS.map((p, i) => (
            <button key={i} data-testid={`thankyou-preset-${i}`} onClick={() => setText(p)}
              className={`text-xs text-left px-3 py-1.5 rounded-2xl border transition-colors ${
                text === p ? "bg-copper text-white border-copper" : "bg-secondary border-border hover:bg-accent text-foreground/80"
              }`}>
              {p.length > 44 ? p.slice(0, 42) + "…" : p}
            </button>
          ))}
        </div>

        <Textarea data-testid="thankyou-text" value={text} onChange={(e) => setText(e.target.value)}
          rows={3} placeholder="Or write your own heartfelt note…" className="rounded-xl resize-none" />

        <Button data-testid="thankyou-send" onClick={send} disabled={sending}
          className="w-full bg-copper hover:bg-copper/90 text-white rounded-full py-6">
          <Sparkles className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send thank-you"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
